import type { Transport } from './transport.js';
import {
  decodeTurn,
  encodeCommands,
  type ClientMessage,
  type LobbyPlayer,
  type MatchSettings,
  type ServerMessage,
} from './protocol.js';

export type LobbyState =
  | { phase: 'connecting' }
  | { phase: 'lobby'; players: LobbyPlayer[]; playerIndex: number; room: string }
  | { phase: 'playing' }
  | { phase: 'error'; message: string };

/**
 * Multiplayer turn source, backed by the relay server.
 *
 * The relay is deliberately dumb: it holds no game state and validates nothing
 * about the commands. Its one job is to release turn N to everyone only once
 * every player has submitted turn N, which is what keeps the simulations in
 * lockstep.
 */
export class WsTransport implements Transport {
  playerIndex = 0;
  settings: MatchSettings = {
    seedLo: 0,
    seedHi: 0,
    mapWidth: 128,
    mapHeight: 128,
    playerCount: 2,
    commandDelay: 4,
  };

  private ws: WebSocket;
  private turnHandler: ((turn: number, perPlayer: Uint8Array[]) => void) | null = null;
  private fatalHandler: ((reason: string) => void) | null = null;
  private lobbyHandler: ((state: LobbyState) => void) | null = null;
  private startHandler: ((settings: MatchSettings, playerIndex: number) => void) | null = null;
  private chatHandler: ((player: number, text: string) => void) | null = null;
  private closedByUs = false;
  /** Turns that arrived before the game was ready to consume them. */
  private queued: Array<{ turn: number; perPlayer: Uint8Array[] }> = [];

  constructor(
    private url: string,
    private room: string,
    private name: string,
  ) {
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.send({ t: 'join', room: this.room, name: this.name });
    };

    this.ws.onmessage = (ev) => this.handleMessage(ev);

    this.ws.onerror = () => {
      this.lobbyHandler?.({ phase: 'error', message: `Could not reach relay at ${this.url}` });
    };

    this.ws.onclose = () => {
      if (this.closedByUs) return;
      this.fatalHandler?.('Connection to the relay was lost.');
      this.lobbyHandler?.({ phase: 'error', message: 'Disconnected from relay.' });
    };
  }

  private handleMessage(ev: MessageEvent): void {
    if (typeof ev.data === 'string') {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      this.handleControl(msg);
      return;
    }
    const turn = decodeTurn(ev.data as ArrayBuffer);
    if (!turn) return;
    if (this.turnHandler) {
      this.turnHandler(turn.turn, turn.perPlayer);
    } else {
      this.queued.push(turn);
    }
  }

  private handleControl(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome':
        this.playerIndex = msg.playerIndex;
        this.lobbyHandler?.({
          phase: 'lobby',
          players: msg.players,
          playerIndex: msg.playerIndex,
          room: msg.room,
        });
        break;
      case 'lobby':
        this.lobbyHandler?.({
          phase: 'lobby',
          players: msg.players,
          playerIndex: this.playerIndex,
          room: this.room,
        });
        break;
      case 'start':
        this.settings = msg.settings;
        this.playerIndex = msg.playerIndex;
        this.lobbyHandler?.({ phase: 'playing' });
        this.startHandler?.(msg.settings, msg.playerIndex);
        break;
      case 'chat':
        this.chatHandler?.(msg.player, msg.text);
        break;
      case 'playerLeft':
        this.fatalHandler?.(`${msg.name} left the match.`);
        break;
      case 'desync':
        // Nothing can be salvaged: the worlds have already diverged.
        this.fatalHandler?.(
          `Desync detected on turn ${msg.turn}. The simulations no longer agree, so the match cannot continue.`,
        );
        break;
      case 'error':
        this.lobbyHandler?.({ phase: 'error', message: msg.message });
        break;
    }
  }

  private send(msg: ClientMessage): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  onLobby(handler: (state: LobbyState) => void): void {
    this.lobbyHandler = handler;
  }

  onStart(handler: (settings: MatchSettings, playerIndex: number) => void): void {
    this.startHandler = handler;
  }

  onChat(handler: (player: number, text: string) => void): void {
    this.chatHandler = handler;
  }

  setReady(ready: boolean): void {
    this.send({ t: 'ready', ready });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  onTurn(handler: (turn: number, perPlayer: Uint8Array[]) => void): void {
    this.turnHandler = handler;
    // Flush anything that arrived while the game was still starting up.
    const pending = this.queued;
    this.queued = [];
    for (const t of pending) handler(t.turn, t.perPlayer);
  }

  onFatal(handler: (reason: string) => void): void {
    this.fatalHandler = handler;
  }

  submit(turn: number, commands: Uint8Array, checksum: bigint): void {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(encodeCommands(turn, commands, checksum));
  }

  close(): void {
    this.closedByUs = true;
    this.ws.close();
  }
}
